import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ExternalLink, Search, ThumbsDown, ThumbsUp } from 'lucide-react';
import axiosInstance from '../utils/axiosInstance';
import { Md, timeAgo } from '../components/bible/rich';
import V2Shell from './V2Shell';

const API = 'api/replai/v2';

const ACTION = {
  posted: { label: 'Draft posted', tone: 'text-[var(--ink)]' },
  posted_low: { label: 'Draft posted, check first', tone: 'text-[var(--ask)]' },
  handoff: { label: 'Note for an agent', tone: 'text-[var(--ink)]' },
  held: { label: 'No note posted', tone: 'text-[var(--ink-2)]' },
  noise: { label: 'Not a customer', tone: 'text-[var(--ink-3)]' },
  failed: { label: 'Failed', tone: 'text-red-700' },
};

const FILTERS = [
  { id: 'all', label: 'Everything' },
  { id: 'posted', label: 'Ready to send' },
  { id: 'check', label: 'Check first' },
  { id: 'agent', label: 'Note for an agent' },
  { id: 'none', label: 'No note' },
  { id: 'noise', label: 'Not a customer' },
  { id: 'failed', label: 'Failed' },
];

const TEAM = [
  { id: '', label: 'Any team action' },
  { id: 'verbatim', label: 'Sent as written' },
  { id: 'adapted', label: 'Sent after edits' },
  { id: 'ignored', label: 'Replaced by the team' },
  { id: 'waiting', label: 'Waiting for the team' },
];

const RATING = [
  { id: '', label: 'Any rating' },
  { id: 'correct', label: 'Rated right' },
  { id: 'incorrect', label: 'Rated wrong' },
  { id: 'none', label: 'Not rated' },
];

const USED_LABEL = { verbatim: 'sent as written', adapted: 'sent after edits', ignored: 'replaced by the team' };

const CATEGORY = (c) => ((c || '').startsWith('noise') ? 'not a customer' : (c || 'unknown').replace(/_/g, ' '));

const Select = ({ value, onChange, children, label }) => (
  <label className="inline-flex items-center">
    <span className="sr-only">{label}</span>
    <select value={value} onChange={(e) => onChange(e.target.value)}
      className={`rounded-md border px-2.5 py-1.5 text-sm focus:outline-none ${value ? 'border-[var(--ink)] bg-[var(--surface)] font-medium' : 'border-[var(--line)] bg-[var(--surface)] text-[var(--ink-2)]'}`}>
      {children}
    </select>
  </label>
);

// New ticket = the customer's first contact, nobody at LampTwist replied yet.
// Follow-up = the customer wrote again after a reply from the team or Replai.
// Priority Replai gives every ticket (Geoff, 2026-10-20); high and critical
// are also raised in Gorgias.
const PRIO = {
  critical: 'border-red-300 bg-red-100 text-red-800', high: 'border-amber-300 bg-amber-100 text-amber-800',
  normal: 'border-[var(--line)] bg-[var(--paper)] text-[var(--ink-2)]', low: 'border-[var(--line-2)] bg-transparent text-[var(--ink-3)]',
};
const Prio = ({ level }) => (PRIO[level]
  ? <span className={`rounded-full border px-2 py-0.5 text-xs font-medium first-letter:uppercase ${PRIO[level]}`} title="Priority Replai gave this ticket">{level}</span>
  : null);

const Kind = ({ kind }) => (kind === 'follow_up'
  ? <span className="rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-800" title="The customer wrote again after a reply from the team or Replai">Follow-up</span>
  : <span className="rounded-full border border-[var(--line)] bg-[var(--paper)] px-2 py-0.5 text-xs font-medium text-[var(--ink-2)]" title="First contact: nobody at LampTwist replied yet">New ticket</span>);

/* ------------------------------------------------------------ list */

const TicketList = () => {
  const [data, setData] = useState(null);
  const [f, setF] = useState({ filter: 'all', team: '', category: '', assigned: '', rating: '', kind: '', priority: '', days: '3', q: '', pipeline: 'v2' });
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const navigate = useNavigate();
  useEffect(() => {
    setData(null);
    const t = setTimeout(() => {
      axiosInstance.get(`${API}/runs/`, { params: f }).then(({ data: d }) => setData(d)).catch(() => setData({ rows: [], facets: { categories: [], agents: [] } }));
    }, f.q ? 250 : 0);
    return () => clearTimeout(t);
  }, [f]);
  const rows = data ? data.rows : null;
  const active = ['team', 'category', 'assigned', 'rating', 'kind', 'priority'].some((k) => f[k]) || f.filter !== 'all' || f.q;

  return (
    <div className="px-6 py-10 sm:px-10 xl:px-14">
      <div>
        <h1 className="text-[2.4rem] font-semibold leading-[1.1]">Tickets</h1>
        <p className="prose-body mt-3 text-[var(--ink-2)]">What Replai did with each ticket, and what the team did with its note.</p>

        <div className="mt-6 flex flex-wrap items-center gap-1">
          {FILTERS.map((x) => (
            <button key={x.id} type="button" onClick={() => set('filter')(x.id)}
              className={`rounded-md px-3 py-1.5 text-sm ${f.filter === x.id ? 'bg-[var(--ink)] text-white' : 'text-[var(--ink-2)] hover:bg-[var(--line-2)]'}`}>
              {x.label}
            </button>
          ))}
          <label className="ml-auto flex items-center gap-2 rounded-md border border-[var(--line)] bg-[var(--surface)] px-3 py-1.5">
            <Search className="h-4 w-4 text-[var(--ink-3)]" />
            <input value={f.q} onChange={(e) => set('q')(e.target.value)} placeholder="R-number, customer, subject or ticket number"
              onKeyDown={(e) => {
                const m = e.key === 'Enter' && f.q.trim().match(/^r?\s*-?\s*(\d{1,6})$/i);
                if (m) navigate(`/tickets/${m[1]}`);  // R-7856 opens the run directly
              }}
              className="w-72 bg-transparent text-sm focus:outline-none" />
          </label>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Select label="Team action" value={f.team} onChange={set('team')}>{TEAM.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}</Select>
          <Select label="New or follow-up" value={f.kind} onChange={set('kind')}>
            <option value="">New and follow-ups</option>
            <option value="new">New tickets</option>
            <option value="follow_up">Follow-ups</option>
          </Select>
          <Select label="Topic" value={f.category} onChange={set('category')}>
            <option value="">Any topic</option>
            {(data?.facets.categories || []).map(([c, n]) => <option key={c} value={c}>{CATEGORY(c).replace(/^./, (m) => m.toUpperCase())} ({n})</option>)}
          </Select>
          <Select label="Assigned to" value={f.assigned} onChange={set('assigned')}>
            <option value="">Anyone</option>
            {(data?.facets.agents || []).map(([a, n]) => <option key={a} value={a}>{a} ({n})</option>)}
          </Select>
          <Select label="Priority" value={f.priority} onChange={set('priority')}>
            <option value="">Any priority</option>
            <option value="urgent">High and critical</option>
            <option value="critical">Critical</option><option value="high">High</option>
            <option value="normal">Normal</option><option value="low">Low</option>
          </Select>
          <Select label="Rating" value={f.rating} onChange={set('rating')}>{RATING.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}</Select>
          <Select label="Period" value={f.days} onChange={set('days')}>
            <option value="1">Today</option><option value="3">Last 3 days</option><option value="7">Last 7 days</option><option value="14">Last 14 days</option>
          </Select>
          {active && (
            <button type="button" onClick={() => setF((x) => ({ ...x, filter: 'all', team: '', category: '', assigned: '', rating: '', kind: '', priority: '', q: '' }))}
              className="text-sm text-[var(--ink-2)] underline hover:text-[var(--ink)]">Clear filters</button>
          )}
          <label className="ml-auto inline-flex items-center gap-2 text-sm text-[var(--ink-2)]">
            <input type="checkbox" checked={f.pipeline === 'all'} onChange={(e) => set('pipeline')(e.target.checked ? 'all' : 'v2')} />
            Include tickets from the old Replai
          </label>
        </div>

        {!rows ? <div className="mt-6 h-64 animate-pulse rounded-lg bg-[var(--line-2)]" /> : !rows.length ? (
          <p className="mt-8 text-[var(--ink-2)]">{f.pipeline === 'v2' && !active ? 'No tickets from the new Replai in this period yet.' : 'No tickets match. Try other filters.'}</p>
        ) : (
          <>
            <p className="num mt-5 text-sm text-[var(--ink-3)]">{rows.length} ticket{rows.length === 1 ? '' : 's'}</p>
            <table className="mt-2 w-full border-collapse text-left">
              <thead>
                <tr className="border-b border-[var(--line)] text-sm text-[var(--ink-3)]">
                  <th className="py-2 pr-4 font-medium">Customer</th>
                  <th className="py-2 pr-4 font-medium">About</th>
                  <th className="py-2 pr-4 font-medium">What Replai did</th>
                  <th className="py-2 pr-4 font-medium">Team</th>
                  <th className="py-2 font-medium">When</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const a = ACTION[r.action] || ACTION.held;
                  const old = r.pipeline !== 'v2' && !r.fallback;
                  return (
                    <tr key={r.run} onClick={() => navigate(`/tickets/${r.run}`)}
                      className={`cursor-pointer border-b border-[var(--line-2)] hover:bg-[var(--surface)] ${old ? 'opacity-55' : ''}`}>
                      <td className="relative py-3 pr-4 pl-3 align-top">
                        {!old && <span className="absolute bottom-3 left-0 top-3 w-[3px] rounded-full bg-[var(--brass)]" aria-hidden />}
                        <span className="block font-medium">{r.customer || 'Unknown'}</span>
                        <span className="line-clamp-1 text-sm text-[var(--ink-2)]">{r.subject}</span>
                        {(r.kind || r.assignee_at_draft || r.priority_level) && (
                          <span className="mt-1 flex flex-wrap gap-1.5">
                            <Prio level={r.priority_level} />
                            {r.kind && <Kind kind={r.kind} />}
                            {r.assignee_at_draft && <span className="rounded-full border border-[var(--line)] px-2 py-0.5 text-xs text-[var(--ink-2)]" title="The ticket was already assigned when Replai drafted">Assigned: {r.assignee_at_draft}</span>}
                          </span>
                        )}
                      </td>
                      <td className="py-3 pr-4 align-top text-[var(--ink-2)] first-letter:uppercase">{CATEGORY(r.category)}</td>
                      <td className="py-3 pr-4 align-top">
                        <span className={a.tone}>{a.label}</span>
                        {r.assigned_to && <span className="block text-sm text-[var(--ink-3)]">Assigned to {r.assigned_to}</span>}
                        <span className="block text-sm text-[var(--ink-3)]">{r.fallback ? 'New Replai failed, old Replai drafted it' : old ? 'Old Replai' : 'New Replai'}</span>
                      </td>
                      <td className="py-3 pr-4 align-top text-sm">
                        {r.human_used && USED_LABEL[r.human_used] ? <span className={r.human_used === 'ignored' ? 'text-[var(--ask)]' : ''}>{USED_LABEL[r.human_used]}</span>
                          : r.action.startsWith('posted') ? <span className="text-[var(--ink-3)]">waiting</span> : <span className="text-[var(--ink-3)]">-</span>}
                        {r.feedback && <span className="block text-[var(--ink-3)]">rated {r.feedback === 'correct' ? 'right' : 'wrong'}</span>}
                      </td>
                      <td className="num py-3 align-top text-sm text-[var(--ink-3)]">{timeAgo(r.created)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------ detail */

const ResultPreview = ({ result }) => {
  if (result == null) return <span className="text-[var(--ink-3)]">no data</span>;
  const text = typeof result === 'string' ? result : JSON.stringify(result, null, 1);
  return <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap rounded bg-[var(--paper)] p-2 text-[0.75rem] text-[var(--ink-2)]">{text.slice(0, 3000)}</pre>;
};

const Draft = ({ title, who, text, meta }) => (
  <section className="rounded-lg border border-[var(--line)] bg-[var(--surface)] p-5">
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <h3 className="font-semibold">{title}</h3>
      {meta && <span className="text-sm text-[var(--ink-3)]">{meta}</span>}
    </div>
    {who && <p className="text-sm text-[var(--ink-3)]">{who}</p>}
    <p className="prose-body mt-3 whitespace-pre-wrap">{text || <span className="text-[var(--ink-3)]">Nothing written.</span>}</p>
  </section>
);

// Quoted history ("On ... wrote:", "Il giorno ... ha scritto:", forwarded
// headers) repeats the whole thread in every message; show it collapsed.
const QUOTE_RE = /\n\s*(?:(?:On|Le|Il giorno|Op|Am|El)\b[^\n]{0,160}\b(?:wrote|a écrit|ha scritto|schreef|schrieb|escribió)\s*:?|-{2,}\s*(?:Original|Forwarded|Message d'origine|Oorspronkelijk)[^\n]*|(?:From|Van|De|Von|Da)\s*:[^\n]*\n[^\n]*(?:Sent|Date|Datum|Envoyé|Gesendet|Verzonden)\s*:)/i;
const splitQuote = (body) => {
  const text = body || '';
  const m = text.match(QUOTE_RE);
  return m ? [text.slice(0, m.index).trim(), text.slice(m.index).trim()] : [text.trim(), ''];
};

const STATUS = {
  high: { label: 'Ready to send', cls: 'bg-green-50 text-green-800 border-green-200' },
  low: { label: 'Check before sending', cls: 'bg-amber-50 text-amber-800 border-amber-200' },
  agent: { label: 'Needs you', cls: 'bg-blue-50 text-blue-800 border-blue-200' },
  none: { label: 'No note posted', cls: 'bg-[var(--line-2)] text-[var(--ink-2)] border-[var(--line)]' },
};

const Pill = ({ s }) => <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-sm font-semibold ${s.cls}`}>{s.label}</span>;

const Card = ({ title, children, right, tone = '' }) => (
  <section className={`rounded-lg border border-[var(--line)] bg-[var(--surface)] p-5 ${tone}`}>
    {(title || right) && (
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        {title && <h3 className="font-semibold">{title}</h3>}
        {right}
      </div>
    )}
    {children}
  </section>
);

const Media = ({ media }) => {
  if (!media) return null;
  const gif = media.video.find((x) => x.gif);
  const stills = media.video.filter((x) => !x.gif);
  return (
    <>
      {media.typo.length > 0 && (
        <Card tone="border-amber-300 bg-amber-50" title="Email typo: check who you are talking to">
          <ul className="list-disc pl-5 text-[0.95rem]">
            {media.typo.map((m) => <li key={m.order_number}>Order {m.order_number} ({m.order_date}{m.total != null ? `, ${m.total} EUR` : ''}) is under <b>{m.email_on_order}</b>, one character away from the customer's email.</li>)}
          </ul>
          <p className="mt-2 text-sm">Share no order details until the customer confirms something only they know, then correct the email on the order.</p>
        </Card>
      )}
      {(gif || stills.length > 0 || media.transcript.length > 0) && (
        <Card title={`Customer's video${media.video[0]?.video ? ` (${media.video[0].video})` : ''}`}>
          <div className="flex flex-wrap items-start gap-3">
            {gif && <a href={gif.url} target="_blank" rel="noreferrer"><img src={gif.url} alt="Video preview" className="w-56 rounded-md border border-[var(--line)]" /></a>}
            <div className="flex flex-wrap gap-2">
              {stills.map((x) => <a key={x.url} href={x.url} target="_blank" rel="noreferrer"><img src={x.url} alt={x.name} className="h-24 rounded border border-[var(--line)]" /></a>)}
            </div>
          </div>
          {media.transcript.map((t) => <p key={t.text} className="mt-3 text-[0.95rem]"><b>Said in the video{t.language ? ` (${t.language})` : ''}:</b> “{t.text}”</p>)}
        </Card>
      )}
    </>
  );
};

const Message = ({ m }) => {
  const [main, quoted] = splitQuote(m.body);
  const imgs = (m.attachments || []).filter((x) => (x.content_type || '').startsWith('image/'));
  const files = (m.attachments || []).filter((x) => !(x.content_type || '').startsWith('image/'));
  return (
    <li className={`rounded-lg p-4 ${m.from_agent ? 'border border-[var(--line)] bg-[var(--surface)]' : 'bg-[var(--line-2)]'}`}>
      <p className="text-sm text-[var(--ink-3)]">{m.from_agent ? 'LampTwist' : 'Customer'} · {m.sender} · {timeAgo(m.sent_at)}</p>
      <p className="mt-1.5 whitespace-pre-wrap text-[0.98rem] leading-relaxed">{main || <span className="text-[var(--ink-3)]">(no new text)</span>}</p>
      {imgs.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {imgs.map((x) => <a key={x.url} href={x.url} target="_blank" rel="noreferrer"><img src={x.url} alt={x.name} className="h-20 rounded border border-[var(--line)] object-cover" /></a>)}
        </div>
      )}
      {files.length > 0 && <p className="mt-2 text-sm text-[var(--ink-2)]">Attached: {files.map((x) => x.name).join(', ')}</p>}
      {quoted && (
        <details className="mt-2">
          <summary className="cursor-pointer text-sm text-[var(--ink-3)]">Show quoted earlier messages</summary>
          <p className="mt-1 whitespace-pre-wrap text-sm text-[var(--ink-3)]">{quoted.slice(0, 4000)}</p>
        </details>
      )}
    </li>
  );
};

const TicketDetail = ({ runId }) => {
  const [d, setD] = useState(null);
  const [failed, setFailed] = useState(false);
  const [note, setNote] = useState('');
  const [tab, setTab] = useState('reply');
  const [showNotes, setShowNotes] = useState(false);
  const load = useCallback(() => {
    axiosInstance.get(`${API}/runs/${runId}/`).then(({ data }) => { setD(data); setNote(data.feedback_note || ''); }).catch(() => setFailed(true));
  }, [runId]);
  useEffect(() => { setD(null); setTab('reply'); load(); }, [load]);

  const rate = async (feedback) => {
    await axiosInstance.patch(`api/replai/dispatch-runs/${runId}/feedback/`, { feedback: d.feedback === feedback ? '' : feedback, note });
    load();
  };

  if (failed) return <p className="px-16 py-10 text-[var(--ink-2)]">This ticket could not be loaded.</p>;
  if (!d) return <div className="m-16 h-64 animate-pulse rounded-lg bg-[var(--line-2)]" />;
  const agent = d.agent;
  const isHandoff = d.audience === 'agent';
  const status = !d.posted_at ? STATUS.none : isHandoff ? STATUS.agent : STATUS[d.band] || STATUS.low;
  const replaiNotes = d.messages.filter((m) => m.channel === 'internal-note');
  const convo = d.messages.filter((m) => m.channel !== 'internal-note');
  const shown = showNotes ? d.messages : convo;
  const hasReview = d.human_reply || d.review || d.v1_draft;
  const TABS = [
    { id: 'reply', label: isHandoff ? 'What to do' : 'Reply' },
    { id: 'conversation', label: `Conversation (${convo.length})` },
    { id: 'how', label: 'How it decided' },
    ...(hasReview ? [{ id: 'review', label: 'Review' }] : []),
  ];

  return (
    <div className="px-6 py-8 sm:px-10 xl:px-14">
      <Link to="/tickets" className="text-sm text-[var(--ink-2)] hover:text-[var(--ink)]">← All tickets</Link>
      <header className="mt-3 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Pill s={status} />
            {!isHandoff && d.confidence != null && <span className="text-sm text-[var(--ink-3)]">confidence {Math.round(d.confidence * 100)}%</span>}
            {d.priority?.set ? <span className="rounded-full border border-red-200 bg-red-50 px-2.5 py-0.5 text-sm font-semibold text-red-800">{d.priority.set} priority</span>
              : <Prio level={d.priority_level} />}
            {d.sent_via_replai?.status === 'sent' && <span className="text-sm text-green-800">Sent by {d.sent_via_replai.by_name}</span>}
            {d.kind && <Kind kind={d.kind} />}
            {d.assignee_at_draft && <span className="rounded-full border border-[var(--line)] px-2 py-0.5 text-xs text-[var(--ink-2)]">Assigned: {d.assignee_at_draft}</span>}
          </div>
          <h1 className="mt-2 text-[1.9rem] font-semibold leading-tight">{d.subject || 'No subject'}</h1>
          <p className="mt-1 text-[var(--ink-2)]">
            R-{d.run} · {d.customer} · <span className="first-letter:uppercase">{CATEGORY(d.category)}</span>
            {d.assigned_to ? ` · assigned to ${d.assigned_to}` : ''}{d.posted_at ? ` · note ${timeAgo(d.posted_at)}` : ''}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {d.links?.send && <a href={d.links.send} target="_blank" rel="noreferrer" className="rounded-md bg-[#166534] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#14532d]">Send to customer</a>}
          {d.links?.copy && <a href={d.links.copy} target="_blank" rel="noreferrer" className="rounded-md border border-[var(--line)] bg-[var(--surface)] px-3 py-1.5 text-sm font-semibold hover:border-[var(--ink-2)]">Copy reply</a>}
          <a href={d.gorgias_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-md border border-[var(--line)] bg-[var(--surface)] px-3 py-1.5 text-sm font-semibold hover:border-[var(--ink-2)]">
            Open in Gorgias <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      </header>

      <nav className="mt-6 flex gap-1 border-b border-[var(--line)]">
        {TABS.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)}
            className={`-mb-px border-b-2 px-3 pb-2 pt-1 text-[0.95rem] ${tab === t.id ? 'border-[var(--ink)] font-semibold text-[var(--ink)]' : 'border-transparent text-[var(--ink-2)] hover:text-[var(--ink)]'}`}>
            {t.label}
          </button>
        ))}
      </nav>

      <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="min-w-0 space-y-5">
          {tab === 'reply' && (
            <>
              {(d.guards?.length > 0 || (d.band === 'low' && d.verifier && !isHandoff)) && (
                <Card tone="border-amber-300 bg-amber-50" title="Check before sending">
                  <ul className="list-disc pl-5 text-[0.95rem]">
                    {(d.guards || []).map((g) => <li key={g}>{g}</li>)}
                    {d.band === 'low' && d.verifier && <li>{d.verifier}</li>}
                  </ul>
                </Card>
              )}
              <Media media={d.media} />
              {isHandoff && d.handoff ? (
                <>
                  <Card title="What's going on"><p className="prose-body whitespace-pre-wrap">{d.handoff.summary}</p></Card>
                  {d.handoff.actions?.length > 0 && (
                    <Card title="What to do">
                      <ol className="list-decimal space-y-1.5 pl-5 text-[0.98rem]">{d.handoff.actions.map((x) => <li key={x}>{x}</li>)}</ol>
                    </Card>
                  )}
                  <Card title="Suggested reply"><p className="prose-body whitespace-pre-wrap">{d.handoff.suggested_reply || <span className="text-[var(--ink-3)]">No reply yet: it depends on what you find.</span>}</p></Card>
                </>
              ) : (
                <Card title={isHandoff ? 'Note for the team' : 'Reply'} right={<span className="text-sm text-[var(--ink-3)]">{d.posted_at ? 'posted in Gorgias' : 'not posted'}</span>}>
                  <p className="prose-body whitespace-pre-wrap">{d.draft || <span className="text-[var(--ink-3)]">Nothing written.</span>}</p>
                </Card>
              )}
              {!d.posted_at && d.auto_post?.reason && <p className="text-sm text-[var(--ink-2)]">Why no note was posted: {d.auto_post.reason}</p>}
            </>
          )}

          {tab === 'conversation' && (
            <>
              {replaiNotes.length > 0 && (
                <label className="flex items-center gap-2 text-sm text-[var(--ink-2)]">
                  <input type="checkbox" checked={showNotes} onChange={(e) => setShowNotes(e.target.checked)} />
                  Show Replai's own notes ({replaiNotes.length})
                </label>
              )}
              <ol className="space-y-3">{shown.map((m, i) => <Message key={i} m={m} />)}</ol>
            </>
          )}

          {tab === 'how' && (
            <>
              {d.reason && <Card title="Why"><Md className="prose-body">{d.reason}</Md></Card>}
              {agent && (
                <Card title={d.agent_is_live ? 'What it looked up' : 'How the Bible agent would answer'}>
                  {!d.agent_is_live && <p className="mb-2 text-sm text-[var(--ink-2)]">Test run next to the current pipeline. Nothing from it was sent or posted.</p>}
                  <ol className="space-y-3">
                    {agent.steps.map((s, i) => (
                      <li key={i} className="border-l-2 border-[var(--line)] pl-4">
                        {s.thought && <p className="text-[0.95rem]">{s.thought}</p>}
                        {s.calls.map((c, j) => (
                          <details key={j} className="mt-1.5">
                            <summary className="cursor-pointer text-sm text-[var(--ink-2)]">
                              <span className="font-medium text-[var(--ink)]">{c.tool.replace(/_/g, ' ')}</span>
                              {Object.values(c.args || {}).length > 0 && ` (${Object.values(c.args).join(', ')})`}
                              {c.error && <span className="text-red-700">, failed</span>}
                            </summary>
                            <ResultPreview result={c.result} />
                          </details>
                        ))}
                      </li>
                    ))}
                  </ol>
                </Card>
              )}
              {d.bible.length > 0 && (
                <Card title="Bible rules it followed">
                  <ul className="space-y-3">
                    {d.bible.map((b) => (
                      <li key={b.id} className="text-[0.95rem]">
                        <Link to={b.path} className="num font-semibold text-[var(--brass)] hover:underline">{b.label}</Link>{' '}
                        {b.kind === 'diagram' ? <span className="text-[var(--ink-2)]">flow chart</span> : <span className="text-[var(--ink-2)]">{b.body}</span>}
                      </li>
                    ))}
                  </ul>
                </Card>
              )}
            </>
          )}

          {tab === 'review' && (
            <>
              {d.human_reply && <Draft title="What the team sent" who={d.human_reply.agent} text={d.human_reply.text} />}
              {d.v1_draft && <Draft title="Current Replai draft" meta={d.v1_draft.audience === 'agent' ? 'note for the team' : `confidence ${Math.round((d.v1_draft.confidence || 0) * 100)}%`} text={d.v1_draft.reply} />}
              {agent && !d.agent_is_live && <Draft title="Bible agent draft" text={agent.reply} />}
              {d.review && (
                <Card title="Review against the team's reply">
                  <p className="text-[0.95rem]">{d.review.v2_correct ? 'The Bible agent got it right.' : 'The Bible agent got it wrong.'} {d.review.summary}</p>
                  {d.review.v2_issues?.length > 0 && <ul className="mt-2 list-disc pl-5 text-[0.95rem] text-[var(--ink-2)]">{d.review.v2_issues.map((i) => <li key={i}>{i}</li>)}</ul>}
                  {d.review.bible_gap && <p className="mt-2 text-[0.95rem] text-[var(--ask)]">Missing from the Bible: {d.review.bible_gap}</p>}
                  {d.review.bible_conflict && <p className="mt-1 text-[0.95rem] text-[var(--ask)]">Bible conflict: {d.review.bible_conflict}</p>}
                </Card>
              )}
            </>
          )}
        </div>

        <aside className="space-y-4 xl:sticky xl:top-6 xl:self-start">
          <Card title="Was this right?">
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="What was wrong, or which Bible rule should change?" className="w-full rounded-md border border-[var(--line)] px-3 py-2 text-[0.95rem] focus:outline-none" />
            <div className="mt-2 flex gap-2">
              <button type="button" onClick={() => rate('correct')} className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm ${d.feedback === 'correct' ? 'border-[var(--ink)] bg-[var(--ink)] text-white' : 'border-[var(--line)]'}`}><ThumbsUp className="h-3.5 w-3.5" /> Right</button>
              <button type="button" onClick={() => rate('incorrect')} className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm ${d.feedback === 'incorrect' ? 'border-[var(--ink)] bg-[var(--ink)] text-white' : 'border-[var(--line)]'}`}><ThumbsDown className="h-3.5 w-3.5" /> Wrong</button>
            </div>
            <p className="mt-2 text-xs text-[var(--ink-3)]">Goes into the Feedback page and the Bible review.</p>
          </Card>
          <Card title="At a glance">
            <dl className="grid grid-cols-[6.5rem_1fr] gap-y-1.5 text-sm">
              <dt className="text-[var(--ink-3)]">Ticket</dt><dd><a href={d.gorgias_url} target="_blank" rel="noreferrer" className="hover:underline">{d.ticket}</a></dd>
              <dt className="text-[var(--ink-3)]">Language</dt><dd>{d.language || '-'}</dd>
              <dt className="text-[var(--ink-3)]">Lookups</dt><dd>{agent ? agent.steps.reduce((n, s) => n + s.calls.length, 0) : 0}{agent?.secs ? `, ${Math.round(agent.secs)} s` : ''}</dd>
              <dt className="text-[var(--ink-3)]">Bible rules</dt><dd>{d.bible.length}</dd>
              <dt className="text-[var(--ink-3)]">Team</dt><dd>{d.human_used === 'no_reply_yet' ? 'no reply yet' : (USED_LABEL[d.human_used] || d.human_used || '-')}</dd>
              {d.other_runs?.length > 0 && <><dt className="text-[var(--ink-3)]">Other notes</dt><dd>{d.other_runs.map((o) => <Link key={o.id} to={`/tickets/${o.id}`} className="mr-2 hover:underline">R-{o.id}</Link>)}</dd></>}
            </dl>
          </Card>
        </aside>
      </div>
    </div>
  );
};

const TicketsV2 = () => {
  const { runId } = useParams();
  return <V2Shell>{runId ? <TicketDetail runId={runId} /> : <TicketList />}</V2Shell>;
};

export default TicketsV2;
